import React, { FunctionComponent, useMemo, useContext, useState, useCallback, useRef } from "react";
import { Canvas } from "react-three-fiber";
import * as THREE from "three";
import { MenuSubcategoryDictionary } from "../../models/categories";
import { GenderDictionary } from "../../models/gender";
import { NutritionContext } from "../../pages/nutrition/context/nutrition-context";
import { CameraController } from "./camera-controller";
import { makeStyles, Theme, useTheme } from "@material-ui/core";
import { ThreeLoadingSuspense } from "../loading/three-loading-suspense";
import { ThreeLoadingIndicator } from "../loading/three-loading-indicator";
import { useBackground } from "../container/background";
import { Gender } from "../../api/smartmelts-api/graphql/generated";
import { DIGITAL_TWIN_MAX_HEIGHT } from "../../theme/theme";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    position: "relative",
  },
}));

function Ground() {
  return (
    <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, -6.5, 0]}>
      <planeBufferGeometry attach="geometry" args={[10, 10]} />
      <shadowMaterial opacity={0.2} />
    </mesh>
  );
}

interface IDigitalTwinProps {
  height: number;
  gender: Gender;
}

const Light = () => {
  const theme = useTheme();
  const light = new THREE.DirectionalLight(new THREE.Color(theme.palette.background.default), 0.8);
  light.position.set(0, 50, 0);
  light.castShadow = true;
  light.shadow.mapSize.width = 200;
  light.shadow.mapSize.height = 200;
  return <primitive object={light} />;
};

export const DigitalTwin: FunctionComponent<IDigitalTwinProps> = (props) => {
  const { height, gender } = props;
  const { activeSubcategory } = useContext(NutritionContext);
  const HumanComponent = useMemo(() => GenderDictionary[gender], [gender]);
  const classes = useStyles();
  const { backgroundRef } = useBackground();
  const humanRef = useRef<THREE.Group>();

  const [loading, setLoading] = useState<boolean>(true);

  const onModelLoaded = useCallback(() => {
    setLoading(false);
  }, [setLoading]);

  return (
    <div style={{ height }} className={classes.container}>
      {loading && <ThreeLoadingIndicator />}
      <Canvas
        camera={{ position: [0, Math.PI / 2, 10], rotation: [Math.PI / 2, Math.PI / 2, 0] }}
        style={{ height, maxHeight: DIGITAL_TWIN_MAX_HEIGHT }}
        onCreated={({ gl }) => {
          gl.shadowMap.enabled = true;
          gl.shadowMap.type = THREE.PCFSoftShadowMap;
        }}
      >
        <ambientLight intensity={0.5} />
        <spotLight intensity={0.4} position={[20, 20, -20]} />
        <spotLight intensity={0.4} position={[20, 20, 20]} />
        <spotLight intensity={0.4} position={[20, 200, 20]} />
        <spotLight intensity={0.4} position={[-20, 200, -20]} />

        <Light />
        <Ground />
        <ThreeLoadingSuspense onModelLoaded={onModelLoaded}>
          {
            <HumanComponent
              humanRef={humanRef}
              activeBodyPart={
                activeSubcategory && MenuSubcategoryDictionary[activeSubcategory]
                  ? MenuSubcategoryDictionary[activeSubcategory].bodypart
                  : undefined
              }
            />
          }
        </ThreeLoadingSuspense>
        <CameraController backgroundRef={backgroundRef} rotatingObject={humanRef} />
      </Canvas>
    </div>
  );
};
